import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Grid,
  MenuItem,
  Select,
  Chip,
  AppBar,
  Toolbar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { useSections } from "./SectionContext";

const sectionRoutes = {
  "Photo, Personal Details": "/PersonalDetail",
  "Websites, Portfolios, Profiles": "/WebsiteSection",
  Certifications: "/Certifications",
  Languages: "/Language",
  Accomplishments: "/AccomplishmentsSection",
  "Additional Information": "/AdditionalInfoForm",
  Software: "/SoftWare",
  References: "/Refferences",
  Volunteering: "/Volunteer",
  Interests: "/Interest",
};

const languageOptions = ["English", "Hindi", "Tamil", "Telugu", "Marathi", "Malayalam"];
const levelOptions = ["Beginner", "Intermediate", "Fluent", "Native"];

const LanguagesSection = () => {
  const [languages, setLanguages] = useState([]);
  const [newLanguage, setNewLanguage] = useState("");
  const [editIndex, setEditIndex] = useState(null);

  const { sections } = useSections();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);

  useEffect(() => {
    if (location.state) {
      const { selectedTemplate: template, selectedTheme: theme } = location.state;
      if (template) setSelectedTemplate(template);
      if (theme) setSelectedTheme(theme);
    }
  }, [location.state]);

  useEffect(() => {
    const saved = localStorage.getItem("languagesList");
    if (saved) setLanguages(JSON.parse(saved));
  }, []);

  const handleQuickAdd = (lang) => {
    if (!languages.find((l) => l.name === lang)) {
      setLanguages([...languages, { name: lang, level: "" }]);
    }
  };

  const handleLevelChange = (index, newLevel) => {
    const updated = [...languages];
    updated[index].level = newLevel;
    setLanguages(updated);
  };

  const handleAddNewLanguage = () => {
    if (newLanguage && !languages.find((l) => l.name === newLanguage)) {
      setLanguages([...languages, { name: newLanguage, level: "" }]);
      setNewLanguage("");
    }
  };

  const handleEdit = (index) => {
    setNewLanguage(languages[index].name);
    setEditIndex(index);
  };

  const handleUpdate = () => {
    if (editIndex !== null && newLanguage.trim()) {
      const updated = [...languages];
      updated[editIndex].name = newLanguage;
      setLanguages(updated);
      setNewLanguage("");
      setEditIndex(null);
    }
  };

  const handleDelete = (index) => {
    const updated = languages.filter((_, i) => i !== index);
    setLanguages(updated);
  };

  const handleSaveAndNext = () => {
    // Save current languages
    localStorage.setItem("languagesList", JSON.stringify(languages));

    // Navigate to next section
    const currentSection = "Languages";
    const currentIndex = sections.indexOf(currentSection);

    if (currentIndex < sections.length - 1) {
      const nextSection = sections[currentIndex + 1];
      navigate(sectionRoutes[nextSection], {
        state: { selectedTemplate, selectedTheme },
      });
    } else {
      navigate("/mainpage", { state: { selectedTemplate, selectedTheme } });
    }
  };

  return (
    <Box sx={{ backgroundColor: "#EFF7FF", minHeight: "100vh" }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: "#002B5B" }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: "bold", letterSpacing: 0.5 }}>
            Languages
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main content */}
      <Box sx={{ p: 4, ml: 0 }}>
        <Typography
          variant="body2"
          sx={{ color: "#0288D1", mb: 1 }}
          gutterBottom
        >
          Include your native language and additional languages you speak.
        </Typography>

        {/* Quick add */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
          {languageOptions.map((lang, idx) => (
            <Button
              key={idx}
              variant="outlined"
              endIcon={<AddIcon />}
              onClick={() => handleQuickAdd(lang)}
              sx={{
                borderRadius: "20px",
                textTransform: "none",
                px: 2,
                borderColor: "#0288D1",
                color: "#002B5B",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#E1F5FE",
                  borderColor: "#002B5B",
                },
              }}
            >
              {lang}
            </Button>
          ))}
        </Box>

        {/* Add custom */}
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <TextField
            label="Add new language"
            variant="outlined"
            size="small"
            value={newLanguage}
            onChange={(e) => setNewLanguage(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#B3D9F7" },
                "&:hover fieldset": { borderColor: "#0288D1" },
                "&.Mui-focused fieldset": { borderColor: "#002B5B" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#002B5B" },
              backgroundColor: "#ffffff",
              borderRadius: 1,
            }}
          />
          {editIndex !== null ? (
            <Button
              variant="contained"
              onClick={handleUpdate}
              sx={{
                backgroundColor: "#002B5B",
                fontWeight: "bold",
                "&:hover": { backgroundColor: "#0288D1" },
                boxShadow: "0 3px 10px rgba(0, 43, 91, 0.3)",
              }}
            >
              Update
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleAddNewLanguage}
              sx={{
                backgroundColor: "#002B5B",
                fontWeight: "bold",
                "&:hover": { backgroundColor: "#0288D1" },
                boxShadow: "0 3px 10px rgba(0, 43, 91, 0.3)",
              }}
            >
              Add
            </Button>
          )}
        </Box>

        {/* Saved languages */}
        <Box>
          {languages.map((lang, idx) => (
            <Card
              key={idx}
              sx={{
                mb: 2,
                boxShadow: "0 2px 10px rgba(2, 136, 209, 0.15)",
                border: "1px solid #B3D9F7",
                borderLeft: "4px solid #002B5B",
                borderRadius: 2,
                backgroundColor: "#ffffff",
                "&:hover": { boxShadow: "0 4px 16px rgba(0, 43, 91, 0.2)" },
              }}
            >
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={4}>
                    <Chip
                      label={lang.name}
                      sx={{
                        backgroundColor: "#002B5B",
                        color: "#ffffff",
                        fontWeight: "bold",
                        "& .MuiChip-label": { px: 1.5 },
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Select
                      fullWidth
                      value={lang.level}
                      displayEmpty
                      onChange={(e) => handleLevelChange(idx, e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#B3D9F7" },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#0288D1" },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#002B5B" },
                        color: "#0D4C8B",
                      }}
                    >
                      <MenuItem value="">Select Level</MenuItem>
                      {levelOptions.map((lvl, i) => (
                        <MenuItem key={i} value={lvl}>
                          {lvl}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>
              </CardContent>
              <CardActions>
                <IconButton
                  onClick={() => handleEdit(idx)}
                  sx={{ color: "#0288D1", "&:hover": { color: "#002B5B" } }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton color="error" onClick={() => handleDelete(idx)}>
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </Box>

        {/* Save & Next Button */}
        <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveAndNext}
            sx={{
              px: 4,
              fontWeight: "bold",
              backgroundColor: "#002B5B",
              "&:hover": { backgroundColor: "#0288D1" },
              borderRadius: 2,
              boxShadow: "0 3px 10px rgba(0, 43, 91, 0.3)",
            }}
          >
            Save & Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default LanguagesSection;